/**
 * LivePollsQuizPanel.jsx
 * ----------------------
 * Sprint LMS-Live · Polls + Quiz inside Live Classroom (Feb 2026).
 *
 * Embedded inside `LiveSessionDetailPage`.  Two stacked tabs (Polls /
 * Quizzes) — trainer view exposes a "Launch new" form and live
 * leaderboard; learner view shows the launched item, accepts the
 * vote/answer, and shows the final result after the trainer closes it.
 *
 * Real-time updates: this component polls every 5s (lightweight) since
 * we already have full HTTP endpoints and the page is already busy
 * with the live video iframe.  WS hookup is a clean follow-up.
 */
import { useCallback, useEffect, useMemo, useState } from "react";import { toast } from "sonner";
import {
  ListChecks, Plus, Play, Lock, Trophy, BarChart3,
  Loader2, Check, X, Sparkles,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  listSessionPolls, createSessionPoll, launchSessionPoll,
  closeSessionPoll, voteSessionPoll,
  listSessionQuizzes, createSessionQuiz, launchSessionQuiz,
  closeSessionQuiz, answerSessionQuiz, getSessionQuizLeaderboard,
  formatApiError,
} from "../../lib/liveClassApi";
import { useRealtime } from "../../hooks/useRealtime";


// Slow safety-net poll once the WebSocket is live — protects against
// dropped or out-of-order frames.  Set lower than the per-tab
// inactivity window so users who background the page for ~1 min
// still resync on focus.
const FALLBACK_POLL_MS = 45000;


export default function LivePollsQuizPanel({ sessionId, isHost }) {
  const [tab, setTab] = useState("polls");

  return (
    <section
      data-testid="live-polls-quiz-panel"
      className="rounded-xl border border-[#0F4C81]/15 bg-white shadow-sm overflow-hidden"
    >
      <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-white via-[#FFFBEC] to-white">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#0A192F] text-[#D4AF37] flex items-center justify-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold">
              Live engagement
            </p>
            <h3 className="font-serif text-xl text-[#0A192F]">
              Polls &amp; Quizzes
            </h3>
          </div>
        </div>
        <nav className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          {[
            { key: "polls",   label: "Polls",   icon: BarChart3 },
            { key: "quizzes", label: "Quizzes", icon: ListChecks },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                aria-pressed={active}
                className={
                  "px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold flex items-center gap-2 transition-colors " +
                  (active
                    ? "bg-[#0A192F] text-white"
                    : "bg-white text-[#0A192F] hover:bg-slate-50")
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <div className="p-6">
        {tab === "polls" ? (
          <PollsPane sessionId={sessionId} isHost={isHost} />
        ) : (
          <QuizzesPane sessionId={sessionId} isHost={isHost} />
        )}
      </div>
    </section>
  );
}


// ════════════════════════════════════════════════════════════════ POLLS ═══
function PollsPane({ sessionId, isHost }) {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const reload = useCallback(async () => {
    try {
      const data = await listSessionPolls(sessionId);
      setPolls(data.polls || []);
    } catch (e) {
      console.warn("polls reload failed:", formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    reload();
    // Fallback poll — much slower than before because the WS subscription
    // below carries the real-time updates.
    const t = setInterval(reload, FALLBACK_POLL_MS);
    return () => clearInterval(t);
  }, [reload]);

  // ── Real-time WS fan-out ─────────────────────────────────────────────
  // Listen on the session-wide control topic (poll_launched / poll_closed)
  // AND on every individual `live_poll:{id}` topic for vote-tally updates.
  // The realtime hook handles auto-reconnect + heartbeat.
  const pollTopics = useMemo(
    () => polls.filter((p) => p.status === "live").map((p) => `live_poll:${p.id}`),
    [polls]
  );
  const allTopics = useMemo(
    () => [`live_session:${sessionId}`, ...pollTopics],
    [sessionId, pollTopics]
  );
  useRealtime(allTopics, (msg) => {
    // Server emits these actions:
    //   live_session:{sid} → poll_launched · poll_closed
    //   live_poll:{pid}    → poll_update (with `results`)
    if (
      msg.action === "poll_launched" ||
      msg.action === "poll_closed" ||
      msg.action === "poll_update"
    ) {
      reload();
    }
  });
  // ──────────────────────────────────────────────────────────────────────

  return (
    <div data-testid="polls-pane">
      {isHost ? (
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            data-testid="poll-launch-toggle"
            className="bg-[#D4AF37] hover:bg-[#b8941d] text-[#0A192F] font-semibold uppercase tracking-[0.16em] text-xs"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {showForm ? "Close form" : "Launch poll"}
          </Button>
        </div>
      ) : null}

      {showForm && isHost ? (
        <CreatePollForm
          sessionId={sessionId}
          busy={creating}
          setBusy={setCreating}
          onCreated={() => {
            setShowForm(false);
            reload();
          }}
        />
      ) : null}

      {loading ? (
        <p className="text-sm text-slate-400">Loading polls…</p>
      ) : polls.length === 0 ? (
        <EmptyState
          label="No polls yet"
          help={
            isHost
              ? "Launch a poll to gather quick reactions or check understanding."
              : "Your trainer hasn't launched a poll in this session yet."
          }
        />
      ) : (
        <div className="grid gap-4">
          {polls.map((p) => (
            <PollCard
              key={p.id}
              poll={p}
              isHost={isHost}
              onChanged={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function CreatePollForm({ sessionId, busy, setBusy, onCreated }) {
  const [question, setQuestion] = useState("");
  const [pollType, setPollType] = useState("multiple_choice");
  const [options, setOptions] = useState(["", ""]);
  const [anonymous, setAnonymous] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (pollType === "multiple_choice" && clean.length < 2) {
      toast.error("Provide at least 2 options.");
      return;
    }
    setBusy(true);
    try {
      await createSessionPoll(sessionId, {
        question: question.trim(),
        poll_type: pollType,
        options: pollType === "yes_no" ? [] : clean,
        anonymous,
        auto_launch: true,
      });
      toast.success("Poll launched");
      onCreated();
      setQuestion("");
      setOptions(["", ""]);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="poll-create-form"
      className="rounded-lg border border-slate-200 bg-slate-50 p-5 mb-4 space-y-4"
    >
      <div>
        <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600">
          Question
        </Label>
        <Textarea
          required
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. Which UN body handles peace & security?"
          data-testid="poll-question-input"
          className="mt-2 bg-white"
          maxLength={400}
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600">
            Type
          </Label>
          <select
            value={pollType}
            onChange={(e) => setPollType(e.target.value)}
            data-testid="poll-type-select"
            className="mt-2 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
          >
            <option value="multiple_choice">Multiple choice</option>
            <option value="yes_no">Yes / No</option>
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
              data-testid="poll-anonymous-input"
            />
            Make votes anonymous
          </label>
        </div>
      </div>
      {pollType === "multiple_choice" ? (
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600">
            Options
          </Label>
          <div className="mt-2 space-y-2">
            {options.map((o, i) => (
              <Input
                key={i}
                value={o}
                onChange={(e) =>
                  setOptions((s) => s.map((v, j) => (j === i ? e.target.value : v)))
                }
                placeholder={`Option ${i + 1}`}
                data-testid={`poll-option-input-${i}`}
                className="bg-white"
                maxLength={120}
              />
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => setOptions((s) => (s.length < 8 ? [...s, ""] : s))}
              data-testid="poll-add-option"
              className="text-xs text-[#0F4C81] hover:underline"
            >
              + Add option
            </button>
            {options.length > 2 ? (
              <button
                type="button"
                onClick={() => setOptions((s) => s.slice(0, -1))}
                className="text-xs text-slate-500 hover:underline"
              >
                Remove last
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={busy}
          data-testid="poll-create-submit"
          className="bg-[#0A192F] hover:bg-[#142844] text-white font-semibold uppercase tracking-[0.16em] text-xs"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
          {busy ? "Launching…" : "Launch poll"}
        </Button>
      </div>
    </form>
  );
}


function PollCard({ poll, isHost, onChanged }) {
  const [voting, setVoting] = useState(false);
  const liveOrClosed = poll.status === "live" || poll.status === "closed";
  const isOpen = poll.status === "live";
  const my = poll.my_vote || null;
  const total = poll.results?.total || 0;

  const vote = async (option) => {
    if (!isOpen) return;
    setVoting(true);
    try {
      await voteSessionPoll(poll.id, option);
      toast.success(`Vote recorded: ${option}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVoting(false);
    }
  };

  const close = async () => {
    try {
      await closeSessionPoll(poll.id);
      toast.success("Poll closed");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <article
      data-testid={`poll-card-${poll.id}`}
      className={
        "rounded-lg border p-5 transition-colors " +
        (isOpen ? "border-[#D4AF37] bg-[#FFFBEC]" : "border-slate-200 bg-white")
      }
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F4C81]">
            {poll.poll_type === "yes_no" ? "Yes / No" : "Multiple choice"}
            {poll.anonymous ? " · Anonymous" : ""}
          </p>
          <h4 className="mt-1 font-semibold text-[#0A192F]">{poll.question}</h4>
        </div>
        <PollBadge status={poll.status} />
      </div>

      <div className="space-y-2">
        {poll.options.map((opt) => {
          const count = poll.results?.counts?.[opt] ?? 0;
          const pct = poll.results?.percentages?.[opt] ?? 0;
          const mine = my === opt;
          return (
            <button
              key={opt}
              type="button"
              disabled={voting || !isOpen || isHost}
              onClick={() => vote(opt)}
              data-testid={`poll-option-${poll.id}-${opt}`}
              aria-pressed={mine}
              className={
                "w-full text-left p-3 rounded-md border relative overflow-hidden transition-colors " +
                (mine
                  ? "border-[#D4AF37] bg-white ring-1 ring-[#D4AF37]"
                  : "border-slate-200 bg-white hover:border-[#0F4C81]") +
                ((!isOpen || isHost) ? " cursor-default" : " cursor-pointer")
              }
            >
              {liveOrClosed ? (
                <span
                  aria-hidden
                  className="absolute inset-y-0 left-0 bg-[#D4AF37]/15"
                  style={{ width: `${pct}%`, transition: "width 0.5s ease" }}
                />
              ) : null}
              <span className="relative flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-[#0A192F]">
                  {mine ? (
                    <Check className="inline h-3.5 w-3.5 mr-1 text-emerald-600" />
                  ) : null}
                  {opt}
                </span>
                {liveOrClosed ? (
                  <span className="text-xs text-slate-600 shrink-0">
                    {count} · {pct}%
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <span>{total} vote{total === 1 ? "" : "s"}</span>
        {isHost && poll.status === "live" ? (
          <button
            type="button"
            onClick={close}
            data-testid={`poll-close-${poll.id}`}
            className="text-xs uppercase tracking-[0.18em] font-bold text-[#0F4C81] hover:underline inline-flex items-center gap-1"
          >
            <Lock className="h-3 w-3" /> Close poll
          </button>
        ) : null}
        {isHost && poll.status === "draft" ? (
          <button
            type="button"
            onClick={async () => {
              try {
                await launchSessionPoll(poll.id);
                toast.success("Poll launched");
                onChanged();
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }}
            data-testid={`poll-launch-${poll.id}`}
            className="text-xs uppercase tracking-[0.18em] font-bold text-[#0F4C81] hover:underline inline-flex items-center gap-1"
          >
            <Play className="h-3 w-3" /> Launch
          </button>
        ) : null}
      </div>
    </article>
  );
}


function PollBadge({ status }) {
  const map = {
    draft:  { label: "Draft",  cls: "bg-slate-100 text-slate-600" },
    live:   { label: "Live",   cls: "bg-emerald-100 text-emerald-700" },
    closed: { label: "Closed", cls: "bg-slate-100 text-slate-500" },
  };
  const v = map[status] || map.draft;
  return (
    <span
      data-testid={`poll-badge-${status}`}
      className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold ${v.cls}`}
    >
      {v.label}
    </span>
  );
}


// ════════════════════════════════════════════════════════════ QUIZZES ═══
function QuizzesPane({ sessionId, isHost }) {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const reload = useCallback(async () => {
    try {
      const data = await listSessionQuizzes(sessionId);
      setQuizzes(data.quizzes || []);
    } catch (e) {
      console.warn("quizzes reload failed:", formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, FALLBACK_POLL_MS);
    return () => clearInterval(t);
  }, [reload]);

  // ── Real-time WS fan-out ─────────────────────────────────────────────
  // Listen on the session-wide control topic (quiz_launched / quiz_closed)
  // AND on every individual `live_quiz:{id}` topic for answer-recorded
  // events (host leaderboard updates live).
  const quizTopics = useMemo(
    () => quizzes.filter((q) => q.status === "live").map((q) => `live_quiz:${q.id}`),
    [quizzes]
  );
  const allTopics = useMemo(
    () => [`live_session:${sessionId}`, ...quizTopics],
    [sessionId, quizTopics]
  );
  useRealtime(allTopics, (msg) => {
    if (
      msg.action === "quiz_launched" ||
      msg.action === "quiz_closed" ||
      msg.action === "answer_recorded"
    ) {
      reload();
    }
  });
  // ──────────────────────────────────────────────────────────────────────

  return (
    <div data-testid="quizzes-pane">
      {isHost ? (
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            data-testid="quiz-launch-toggle"
            className="bg-[#D4AF37] hover:bg-[#b8941d] text-[#0A192F] font-semibold uppercase tracking-[0.16em] text-xs"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {showForm ? "Close form" : "Launch quiz"}
          </Button>
        </div>
      ) : null}

      {showForm && isHost ? (
        <CreateQuizForm
          sessionId={sessionId}
          onCreated={() => {
            setShowForm(false);
            reload();
          }}
        />
      ) : null}

      {loading ? (
        <p className="text-sm text-slate-400">Loading quizzes…</p>
      ) : quizzes.length === 0 ? (
        <EmptyState
          label="No quizzes yet"
          help={
            isHost
              ? "Build a quick multiple-choice quiz, launch it live, and the leaderboard appears the moment you close it."
              : "Your trainer hasn't launched a quiz in this session yet."
          }
        />
      ) : (
        <div className="space-y-5">
          {quizzes.map((q) => (
            <QuizCard
              key={q.id}
              quiz={q}
              isHost={isHost}
              onChanged={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function CreateQuizForm({ sessionId, onCreated }) {
  const [title, setTitle] = useState("");
  const [questions, setQuestions] = useState([
    { prompt: "", options: ["", "", "", ""], correct_option: "", points: 1 },
  ]);
  const [busy, setBusy] = useState(false);

  const updateQ = (i, patch) =>
    setQuestions((s) => s.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  const updateOption = (i, oi, value) =>
    setQuestions((s) =>
      s.map((q, j) =>
        j === i
          ? { ...q, options: q.options.map((o, k) => (k === oi ? value : o)) }
          : q
      )
    );

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = questions.map((q) => ({
      prompt: q.prompt.trim(),
      options: q.options.map((o) => o.trim()).filter(Boolean),
      correct_option: q.correct_option.trim(),
      points: Number(q.points) || 1,
    }));
    if (cleaned.some((q) => !q.prompt || q.options.length < 2 || !q.correct_option)) {
      toast.error("Each question needs a prompt, ≥2 options and a correct answer.");
      return;
    }
    if (cleaned.some((q) => !q.options.includes(q.correct_option))) {
      toast.error("Correct option must match one of the entered options exactly.");
      return;
    }
    setBusy(true);
    try {
      await createSessionQuiz(sessionId, {
        title: title.trim() || "Live quiz",
        questions: cleaned,
        auto_launch: true,
      });
      toast.success("Quiz launched");
      onCreated();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="quiz-create-form"
      className="rounded-lg border border-slate-200 bg-slate-50 p-5 mb-4 space-y-5"
    >
      <div>
        <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600">
          Quiz title
        </Label>
        <Input
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Procedure Pulse Check"
          data-testid="quiz-title-input"
          className="mt-2 bg-white"
          maxLength={200}
        />
      </div>
      {questions.map((q, i) => (
        <div
          key={i}
          data-testid={`quiz-question-${i}`}
          className="rounded-md border border-slate-200 bg-white p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F4C81]">
              Question {i + 1}
            </p>
            {questions.length > 1 ? (
              <button
                type="button"
                onClick={() =>
                  setQuestions((s) => s.filter((_, j) => j !== i))
                }
                className="text-xs text-rose-600 hover:underline"
                data-testid={`quiz-remove-q-${i}`}
              >
                Remove
              </button>
            ) : null}
          </div>
          <Textarea
            required
            value={q.prompt}
            onChange={(e) => updateQ(i, { prompt: e.target.value })}
            placeholder="What's the question?"
            data-testid={`quiz-question-prompt-${i}`}
            maxLength={500}
          />
          <div className="grid sm:grid-cols-2 gap-2">
            {q.options.map((opt, oi) => (
              <Input
                key={oi}
                value={opt}
                onChange={(e) => updateOption(i, oi, e.target.value)}
                placeholder={`Option ${oi + 1}`}
                data-testid={`quiz-option-input-${i}-${oi}`}
                maxLength={200}
              />
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600">
                Correct answer
              </Label>
              <select
                required
                value={q.correct_option}
                onChange={(e) => updateQ(i, { correct_option: e.target.value })}
                data-testid={`quiz-correct-select-${i}`}
                className="mt-2 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="" disabled>
                  Choose…
                </option>
                {q.options
                  .map((o) => o.trim())
                  .filter(Boolean)
                  .map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600">
                Points
              </Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={q.points}
                onChange={(e) => updateQ(i, { points: e.target.value })}
                data-testid={`quiz-points-input-${i}`}
                className="mt-2 bg-white"
              />
            </div>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() =>
            setQuestions((s) =>
              s.length < 25
                ? [
                    ...s,
                    {
                      prompt: "",
                      options: ["", "", "", ""],
                      correct_option: "",
                      points: 1,
                    },
                  ]
                : s
            )
          }
          data-testid="quiz-add-question"
          className="text-xs uppercase tracking-[0.16em] font-bold text-[#0F4C81] hover:underline"
        >
          + Add another question
        </button>
        <Button
          type="submit"
          disabled={busy}
          data-testid="quiz-create-submit"
          className="bg-[#0A192F] hover:bg-[#142844] text-white font-semibold uppercase tracking-[0.16em] text-xs"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
          {busy ? "Launching…" : "Launch quiz"}
        </Button>
      </div>
    </form>
  );
}


function QuizCard({ quiz, isHost, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const isLive = quiz.status === "live";
  const isClosed = quiz.status === "closed";
  const myAnswers = useMemo(() => {
    const m = {};
    (quiz.my_answers || []).forEach((a) => {
      m[a.question_id] = a;
    });
    return m;
  }, [quiz.my_answers]);

  const answer = async (qid, choice) => {
    if (!isLive) return;
    setBusy(true);
    try {
      await answerSessionQuiz(quiz.id, qid, choice);
      toast.success("Answer recorded");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    try {
      await closeSessionQuiz(quiz.id);
      toast.success("Quiz closed — leaderboard ready");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadLeaderboard = useCallback(async () => {
    if (!isClosed && !isHost) return;
    try {
      const lb = await getSessionQuizLeaderboard(quiz.id);
      setLeaderboard(lb);
    } catch (e) {
      console.warn("leaderboard load failed:", formatApiError(e));
    }
  }, [quiz.id, isClosed, isHost]);

  useEffect(() => {
    if (isClosed || isHost) loadLeaderboard();
  }, [isClosed, isHost, loadLeaderboard]);

  return (
    <article
      data-testid={`quiz-card-${quiz.id}`}
      className={
        "rounded-lg border p-5 " +
        (isLive
          ? "border-[#D4AF37] bg-[#FFFBEC]"
          : "border-slate-200 bg-white")
      }
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F4C81]">
            Live quiz · {quiz.total_points || 0} pts
          </p>
          <h4 className="mt-1 font-serif text-xl text-[#0A192F]">
            {quiz.title}
          </h4>
        </div>
        <PollBadge status={quiz.status} />
      </div>

      <div className="space-y-4">
        {(quiz.questions || []).map((q, qi) => {
          const mine = myAnswers[q.id];
          return (
            <div
              key={q.id}
              data-testid={`quiz-question-card-${q.id}`}
              className="rounded-md border border-slate-200 bg-white p-4"
            >
              <p className="text-sm font-semibold text-[#0A192F]">
                {qi + 1}. {q.prompt}
              </p>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {q.options.map((opt) => {
                  const chosen = mine?.choice === opt;
                  const wasCorrect =
                    isClosed && q.correct_option === opt;
                  const wasWrong =
                    isClosed && chosen && q.correct_option !== opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={busy || !isLive || isHost}
                      onClick={() => answer(q.id, opt)}
                      data-testid={`quiz-answer-${q.id}-${opt}`}
                      aria-pressed={chosen}
                      className={
                        "text-left px-3 py-2 rounded-md border text-sm transition-colors " +
                        (wasCorrect
                          ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                          : wasWrong
                          ? "border-rose-300 bg-rose-50 text-rose-800"
                          : chosen
                          ? "border-[#D4AF37] bg-[#FFFBEC] text-[#0A192F]"
                          : "border-slate-200 bg-white text-[#0A192F] hover:border-[#0F4C81]") +
                        (!isLive || isHost ? " cursor-default" : " cursor-pointer")
                      }
                    >
                      {wasCorrect ? (
                        <Check className="inline h-3.5 w-3.5 mr-1 text-emerald-600" />
                      ) : wasWrong ? (
                        <X className="inline h-3.5 w-3.5 mr-1 text-rose-600" />
                      ) : chosen ? (
                        <Check className="inline h-3.5 w-3.5 mr-1 text-[#D4AF37]" />
                      ) : null}
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Host controls */}
      {isHost && isLive ? (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={close}
            data-testid={`quiz-close-${quiz.id}`}
            className="text-xs uppercase tracking-[0.18em] font-bold text-[#0F4C81] hover:underline inline-flex items-center gap-1"
          >
            <Lock className="h-3 w-3" /> Close quiz &amp; reveal leaderboard
          </button>
        </div>
      ) : null}

      {/* Leaderboard */}
      {(isClosed || isHost) && leaderboard ? (
        <Leaderboard rows={leaderboard.leaderboard || []} />
      ) : null}
    </article>
  );
}


function Leaderboard({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div
      data-testid="quiz-leaderboard"
      className="mt-5 rounded-md border border-[#0F4C81]/15 bg-gradient-to-br from-white to-[#FFFBEC] p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="h-4 w-4 text-[#D4AF37]" />
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0A192F]">
          Leaderboard
        </p>
      </div>
      <ol className="space-y-1">
        {rows.slice(0, 10).map((r) => (
          <li
            key={r.learner_id}
            data-testid={`leaderboard-row-${r.rank}`}
            className="grid grid-cols-[2rem_1fr_auto_auto] items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white"
          >
            <span
              className={
                "text-xs font-bold " +
                (r.rank === 1
                  ? "text-[#D4AF37]"
                  : r.rank === 2
                  ? "text-slate-500"
                  : r.rank === 3
                  ? "text-amber-700"
                  : "text-slate-400")
              }
            >
              #{r.rank}
            </span>
            <span className="text-sm font-medium text-[#0A192F] truncate">
              {r.learner_name}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
              {r.correct}/{r.attempted}
            </span>
            <span className="text-sm font-semibold text-[#0F4C81]">
              {r.score}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}


function EmptyState({ label, help }) {
  return (
    <div className="text-center py-12 px-4 border border-dashed border-slate-300 rounded-sm bg-white">
      <div className="relative inline-flex items-center justify-center mb-4">
        <div aria-hidden className="absolute inset-0 rounded-full bg-brand-gold/15 blur-2xl animate-gold-pulse" />
        <div className="relative h-11 w-11 rounded-sm bg-brand-navy-dark text-brand-gold flex items-center justify-center shadow-gold-soft">
          <Sparkles className="h-5 w-5" />
        </div>
      </div>
      <p className="eyebrow text-brand-gold-dark">Nothing here yet</p>
      <h3 className="mt-2 font-display text-lg text-brand-navy-dark">{label}</h3>
      {help ? <p className="mt-2 text-xs text-slate-500 max-w-md mx-auto leading-relaxed">{help}</p> : null}
    </div>
  );
}
